<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Tutorial/How_to_Handle_Typing';
    let nextLink= '/Tutorial/Conclusion';
    
    import Figure from '$lib/figures/Figure.svelte';
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="validating-the-model-1"
  bind:intersecting={visibleById["validating-the-model-1"]}
  onIntersectChange={(visible) => setVisible("validating-the-model-1", visible)}
>Validating the Model</SectionComponent>
<p>In the previous lesson, we learned how to add typings to the model, but we didn’t yet use them to provide user feedback.<br>
In this lesson, we’ll use those typings to <strong>validate the model</strong> and produce <strong>type errors</strong>.</p>
<SectionComponent tag="h2" 
  id="adding-type-checking-2"
  bind:intersecting={visibleById["adding-type-checking-2"]}
  onIntersectChange={(visible) => setVisible("adding-type-checking-2", visible)}
>Adding Type Checking</SectionComponent>
<p>Freon automatically generates a <strong>validator</strong> for your DSL that includes several built-in checks.<br>
For example, it verifies that all references point to valid targets and that every property with the type <code>identifier</code> has a valid TypeScript identifier as its value.</p>
<p>However, Freon doesn’t include built-in checks for <strong>types</strong>—you need to define those yourself.<br>
Let’s take the <code>AndExpression</code> concept. It has both a left-hand and a right-hand side, and both should have the type <strong>Boolean</strong>.<br>
We can define a similar rule for <code>OrExpression</code>.</p>
<p>Add a file called <code>edu.valid</code> and include the following definitions:</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson9-defs/edu.valid#L1-L11

validator for language Education

AndExpression &#123;
    typecheck equalsType( self.left, #PrimitiveType:Boolean );
    typecheck equalsType( self.right, #PrimitiveType:Boolean );
&#125;

OrExpression &#123;
    typecheck equalsType( self.left, #PrimitiveType:Boolean );
    typecheck equalsType( self.right, #PrimitiveType:Boolean );
&#125;</code></pre>
<p>Now, what about instances of <code>LessOrEqualsExpression</code> and the other comparison expressions?<br>
They also have left and right sides, but for them, it’s not about a specific type—it’s about <strong>both sides having the same type</strong>.<br>
We can define that as follows:</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson9-defs/edu.valid#L13-L31

LessOrEqualsExpression &#123;
    typecheck equalsType( self.left, self.right );
&#125;

GreaterOrEqualsExpression &#123;
    typecheck equalsType( self.left, self.right );
&#125;

LessThenExpression &#123;
    typecheck equalsType( self.left, self.right );
&#125;

GreaterThenExpression &#123;
    typecheck equalsType( self.left, self.right );
&#125;

EqualsExpression &#123;
    typecheck equalsType( self.left, self.right );
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="the-result-3"
  bind:intersecting={visibleById["the-result-3"]}
  onIntersectChange={(visible) => setVisible("the-result-3", visible)}
>The Result</SectionComponent>
<p>Once validation is configured, Freon can identify type mismatches and display errors in the editor.</p>
<p>Currently, validation doesn’t run automatically—you need to activate it manually.<br>
Open the <strong>Edit</strong> menu and select <strong>Validate</strong> to trigger the checks.<br>
When you do, you’ll see something like this:</p>
<Figure
imageName={'tutorial/Tutorial-lesson9-screenshot1.png'}
caption={'Validation error in grading expression'}
figureNumber={1}
/>


<PrevNextSection {prevLink} {nextLink} />